import { useState, useMemo, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Search, ArrowUpDown, Loader2, RefreshCw, X, User as UserIcon, Sparkles, Video, Calendar } from "lucide-react";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { Badge } from "@/shared/components/ui/badge";
import { Card, CardContent } from "@/shared/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/shared/components/ui/dialog";
import { ScrollArea } from "@/shared/components/ui/scroll-area";
import { Label } from "@/shared/components/ui/label";
import { Textarea } from "@/shared/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger } from "@/shared/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/shared/components/ui/select";
import { useCallHistory, CallHistoryItem } from "@/shared/hooks/useCallHistory";
import { useToast } from "@/shared/hooks/use-toast";
import { useSubscriptionGuard } from "@/shared/hooks/useSubscriptionGuard";
import { SubscriptionRequiredScreen } from "@/features/billing/components/SubscriptionRequiredScreen";
import { VideoPlayerModal } from "@/shared/components/VideoPlayerModal";
import { ImageModal } from "@/shared/components/ImageModal";
import { apiClient } from "@/shared/services/apiClient";
import { API_ROUTES } from "@/shared/services/apiRoutes";
import { useTeamMembers } from "@/shared/hooks/useTeamMembers";
import { supabase } from "@/integrations/supabase/client";
import { useIncomingRequests, type IncomingRequest } from "@/shared/hooks/useIncomingRequests";

export default function CallsPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const { canAccess, reason } = useSubscriptionGuard();

  // Filter state
  const [filterByUser, setFilterByUser] = useState<string | undefined>(undefined);

  // Data loading
  const { calls, loading, error, refresh, refreshing, hasMore, loadMore } = useCallHistory({
    limit: 50,
    enablePolling: true,
    pollInterval: 20000, // 20 seconds
    filterByUser,
  });

  const {
    requests: incomingRequests,
    loading: loadingIncoming,
    refresh: refreshIncoming,
  } = useIncomingRequests({ enablePolling: true });

  // Team members for filter dropdown
  const { teamMembers, loading: loadingTeam } = useTeamMembers();

  // Local state
  const [searchQuery, setSearchQuery] = useState("");
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc' | null>('desc');
  const [activeTab, setActiveTab] = useState<'all' | 'recording-request' | 'incoming-request'>('all');
  const [selectedCall, setSelectedCall] = useState<CallHistoryItem | null>(null);
  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  const [showVideoPlayer, setShowVideoPlayer] = useState(false);
  const [videoCallDetails, setVideoCallDetails] = useState<CallHistoryItem | null>(null);

  // User role state (for UI display only - backend handles actual filtering)
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const [userRole, setUserRole] = useState<'admin' | 'user' | null>(null);

  // AI Summary and call data states
  const [aiSummary, setAiSummary] = useState<string[]>([]);
  const [aiTranscript, setAiTranscript] = useState<string | null>(null);
  const [callNotes, setCallNotes] = useState<string | null>(null);
  const [snapshots, setSnapshots] = useState<Array<{ name: string; url: string }>>([]);
  const [loadingCallData, setLoadingCallData] = useState(false);
  const [selectedImage, setSelectedImage] = useState<{ name: string; url: string } | null>(null);
  const [allRecordings, setAllRecordings] = useState<Array<{ id: string; url: string | null; duration_seconds: number | null; status: string | null; created_at: string }>>([]);

  // Auto-save notes with debouncing (500ms delay) - Using backend API
  useEffect(() => {
    if (!selectedCall?.room_id || !callNotes) return;

    const timer = setTimeout(async () => {
      try {
        await apiClient.patch(API_ROUTES.SESSION_NOTES_UPDATE(selectedCall.room_id), { notes: callNotes });
        console.log("✅ Call notes auto-saved via backend API");
      } catch (error) {
        console.error("Error auto-saving call notes:", error);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [callNotes, selectedCall?.room_id]);

  /**
   * Get badge color and label based on call type
   */
  const getCallTypeBadge = (type: string) => {
    switch (type) {
      case 'recording-request':
        return { label: 'Recording', color: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300' };
      case 'start-now':
        return { label: 'Live Call', color: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300' };
      case 'scheduled':
        return { label: 'Scheduled', color: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300' };
      default:
        return { label: 'Call', color: 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-300' };
    }
  };

  /**
   * Format date and time
   */
  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  /**
   * Format duration - simple format without "minutes" text
   */
  const formatDuration = (seconds: number | null) => {
    if (!seconds) return 'N/A';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    // Just show numbers with m/s units, no "minutes" text
    if (secs === 0) return `${mins}m`;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  /**
   * Get duration for a call - tries duration_seconds first, then calculates from timestamps
   */
  const getCallDuration = (call: CallHistoryItem): number | null => {
    // Try duration_seconds first (most accurate)
    if (call.duration_seconds) {
      return call.duration_seconds;
    }

    // Fallback: Calculate from start_time and ended_at
    if (call.start_time && call.ended_at) {
      const start = new Date(call.start_time).getTime();
      const end = new Date(call.ended_at).getTime();
      const durationMs = end - start;
      if (durationMs > 0) {
        return Math.floor(durationMs / 1000); // Convert to seconds
      }
    }

    // Fallback: Calculate from created_at and ended_at for start-now calls
    if (call.created_at && call.ended_at && call.type === 'start-now') {
      const start = new Date(call.created_at).getTime();
      const end = new Date(call.ended_at).getTime();
      const durationMs = end - start;
      if (durationMs > 0) {
        return Math.floor(durationMs / 1000); // Convert to seconds
      }
    }

    return null;
  };

  /**
   * Fetch current user and role
   */
  useEffect(() => {
    const fetchUserRole = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      setCurrentUserId(user.id);

      // SECURITY: Use backend API instead of direct database query
      const userData = await apiClient.get<{ ok: boolean; userRole: { role?: string } | null }>('/user/me');
      if (userData?.userRole?.role) {
        setUserRole(userData.userRole.role as 'admin' | 'user');
      }
    };

    fetchUserRole();
  }, []);

  /**
   * Handle URL params for direct navigation (notifications)
   */
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const tab = searchParams.get('tab');
    const recordingRoomId = searchParams.get('recording');

    // Set active tab if specified
    if (tab === 'recording-request') {
      setActiveTab('recording-request');
    }
    if (tab === 'incoming-request') {
      setActiveTab('incoming-request');
    }

    // Auto-open recording if specified (Incoming Requests or notifications)
    if (recordingRoomId) {
      if (tab === 'incoming-request') {
        openRecordingByRoomId(recordingRoomId);
      } else if (calls.length > 0) {
        const call = calls.find(c => c.room_id === recordingRoomId);
        if (call) {
          loadCallDetails(call);
        } else {
          openRecordingByRoomId(recordingRoomId);
        }
      }
    }
  }, [location.search, calls]);

  /**
   * Search and filter calls
   * NOTE: Backend already filters by company and role, we only do search and sort here
   */
  const filteredAndSortedCalls = useMemo(() => {
    let filtered = [...calls];

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(call =>
        call.customer_name?.toLowerCase().includes(query) ||
        call.room_id?.toLowerCase().includes(query) ||
        call.phone_number?.toLowerCase().includes(query) ||
        call.customer_phone?.toLowerCase().includes(query) ||
        call.email?.toLowerCase().includes(query) ||
        call.customer_email?.toLowerCase().includes(query)
      );
    }

    // Apply sorting
    if (sortOrder) {
      filtered.sort((a, b) => {
        const dateA = new Date(a.start_time || a.created_at).getTime();
        const dateB = new Date(b.start_time || b.created_at).getTime();
        return sortOrder === 'asc' ? dateA - dateB : dateB - dateA;
      });
    }

    return filtered;
  }, [calls, searchQuery, sortOrder]);

  /**
   * Filter calls by active tab
   */
  const tabFilteredCalls = useMemo(() => {
    if (activeTab === 'all') return filteredAndSortedCalls;
    return filteredAndSortedCalls.filter(call => call.type === 'recording-request');
  }, [activeTab, filteredAndSortedCalls]);

  /**
   * Toggle sort order
   */
  const toggleSort = () => {
    setSortOrder(prev => {
      if (prev === null) return 'desc';
      if (prev === 'desc') return 'asc';
      return null;
    });
  };

  /**
   * Open the existing VideoPlayerModal for a recording by room ID
   */
  const openRecordingByRoomId = async (
    roomId: string,
    fallbackDetails?: { customerName?: string; phone?: string; project?: string | null }
  ) => {
    const stubCall: CallHistoryItem = {
      id: roomId,
      type: 'recording-request',
      room_id: roomId,
      customer_name: fallbackDetails?.customerName || 'Customer',
      phone_number: fallbackDetails?.phone,
      project: fallbackDetails?.project || undefined,
      has_recording: true,
      created_at: new Date().toISOString(),
    };

    setVideoCallDetails(stubCall);
    setLoadingCallData(true);

    try {
      const response = await apiClient.get<{
        ok: boolean;
        recording: { url: string | null; duration_seconds: number | null; status: string | null } | null;
      }>(API_ROUTES.CALL_DETAILS(roomId));

      if (response.ok && response.recording?.url) {
        setVideoUrl(response.recording.url);
        setShowVideoPlayer(true);
      } else {
        toast({
          title: 'No Recording',
          description: 'This recording has not been submitted yet.',
          variant: 'destructive',
        });
      }
    } catch (err: any) {
      console.error('Error loading recording:', err);
      toast({
        title: 'Error',
        description: 'Failed to load recording',
        variant: 'destructive',
      });
    } finally {
      setLoadingCallData(false);
    }
  };

  /**
   * Load all call details including AI summary, notes, snapshots, and recording
   */
  const loadCallDetails = async (call: CallHistoryItem) => {
    // For recording-request type, show video player modal directly if recording exists
    if (call.type === 'recording-request' && call.has_recording) {
      setVideoCallDetails(call);
      setLoadingCallData(true);

      try {
        const response = await apiClient.get<{
          ok: boolean;
          session: any;
          recording: {
            url: string | null;
            duration_seconds: number | null;
            status: string | null;
          } | null;
          snapshots: Array<{ name: string; url: string | null }>;
        }>(API_ROUTES.CALL_DETAILS(call.room_id));

        if (response.ok && response.recording && response.recording.url) {
          setVideoUrl(response.recording.url);
          setShowVideoPlayer(true);
        } else {
          toast({
            title: "No Recording",
            description: "This recording request has not been submitted yet.",
            variant: "destructive",
          });
        }
      } catch (err: any) {
        console.error('Error loading recording:', err);
        toast({
          title: "Error",
          description: "Failed to load recording",
          variant: "destructive",
        });
      } finally {
        setLoadingCallData(false);
      }
      return;
    }

    // For other call types, show regular call details modal
    setSelectedCall(call);
    setLoadingCallData(true);
    setAiSummary([]);
    setAiTranscript(null);
    setCallNotes(null);
    setSnapshots([]);
    setVideoUrl(null);

    try {
      // Call backend API to get all call details
      const response = await apiClient.get<{
        ok: boolean;
        session: {
          id: string;
          room_id: string;
          customer_id: string | null;
          customer_name: string | null;
          customer_phone: string | null;
          customer_email: string | null;
          customer_project: string | null;
          start_time: string;
          end_time: string | null;
          duration_seconds: number | null;
          notes: string | null;
          ai_summary: any;
          ai_transcript: string | null;
          ai_summary_status: string | null;
        };
        recording: {
          url: string | null;
          duration_seconds: number | null;
          status: string | null;
        } | null;
        recordings?: Array<{
          id: string;
          url: string | null;
          duration_seconds: number | null;
          status: string | null;
          created_at: string;
        }>;
        snapshots: Array<{ name: string; url: string | null }>;
      }>(API_ROUTES.CALL_DETAILS(call.room_id));

      if (response.ok && response.session) {
        // Debug: Log customer_id from response
        console.log('[CallsPage] Call details response customer_id:', response.session.customer_id);

        // Update selectedCall with customer_id and fresh recording status from the detailed response
        const hasRecordingFromDetails = (response.recordings && response.recordings.length > 0) || !!(response.recording && response.recording.url);
        setSelectedCall({
          ...call,
          customer_id: response.session.customer_id || undefined,
          has_recording: call.has_recording || hasRecordingFromDetails,
          recording_status: response.session.recording_status || call.recording_status,
        });

        // Set notes
        setCallNotes(response.session.notes || null);

        // Set AI summary
        if (response.session.ai_summary) {
          if (Array.isArray(response.session.ai_summary)) {
            setAiSummary(response.session.ai_summary as string[]);
          } else {
            try {
              const parsed = JSON.parse(JSON.stringify(response.session.ai_summary));
              setAiSummary(Array.isArray(parsed) ? parsed : []);
            } catch {
              setAiSummary([]);
            }
          }
        }

        // Set AI transcript
        if (response.session.ai_transcript) {
          setAiTranscript(response.session.ai_transcript);
        }

        // Set snapshots
        if (response.snapshots) {
          setSnapshots(response.snapshots.filter(s => s.url) as Array<{ name: string; url: string }>);
        }

        // Set all recordings (for multiple recordings support)
        console.log('[CallsPage] API response recordings:', response.recordings);
        console.log('[CallsPage] API response recording:', response.recording);
        if (response.recordings && response.recordings.length > 0) {
          console.log('[CallsPage] Setting allRecordings:', response.recordings);
          setAllRecordings(response.recordings);
          // Set first recording URL for backwards compatibility
          setVideoUrl(response.recordings[0].url);
        } else if (response.recording && response.recording.url) {
          // Fallback to single recording for backwards compatibility
          console.log('[CallsPage] Using single recording fallback');
          setVideoUrl(response.recording.url);
          setAllRecordings([]);
        }
      }
    } catch (err: any) {
      console.error('Error loading call details:', err);
      console.error('Error type:', typeof err, 'Error object:', JSON.stringify(err, null, 2));

      let errorMessage = 'Failed to load call details';
      if (err instanceof Error) {
        errorMessage = err.message;
      } else if (typeof err === 'string') {
        errorMessage = err;
      } else if (err?.message) {
        errorMessage = err.message;
      } else if (err?.error) {
        errorMessage = err.error;
      }

      console.error('Final error message:', errorMessage);
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setLoadingCallData(false);
    }
  };

  /**
   * Handle call click
   */
  const handleCallClick = async (call: CallHistoryItem) => {
    // ✅ NEW: For scheduled calls that have ENDED, show call details instead of notification modal
    const hasEnded = (call as any).ended_at || call.duration_seconds;

    // For scheduled/upcoming calls (not ended), open the CallNotificationModal
    if ((call.type === 'scheduled' || call.status === 'scheduled') && !hasEnded) {
      try {
        // SECURITY: Use backend API instead of direct database query
        const response = await apiClient.get<{ ok: boolean; events: any[] }>(`/calendar/events?room_id=${call.room_id}`);
        const calendarEvent = response?.events?.[0];

        if (calendarEvent) {
          // Navigate with event parameter to trigger CallNotificationModal
          navigate(`?event=${calendarEvent.id}`);
          return;
        }
      } catch (err) {
        console.error('[CallsPage] Error finding calendar event:', err);
      }
    }

    // For completed/past calls (or ended scheduled calls), show the detailed call history modal
    loadCallDetails(call);
  };

  /**
   * Handle view customer action - navigate directly to full profile
   */
  const handleViewCustomer = async (call: CallHistoryItem, e?: React.MouseEvent) => {
    e?.stopPropagation();

    console.log('[CallsPage] handleViewCustomer called with customer_id:', call.customer_id);

    // If we have customer_id, navigate directly to full profile
    if (call.customer_id) {
      // Close call details modal first
      setSelectedCall(null);
      // Navigate directly to customer profile page
      navigate(`/contacts?customer=${call.customer_id}`);
    } else {
      toast({
        title: "Customer Information Not Available",
        description: "No customer information is associated with this call.",
        variant: "destructive",
      });
    }
  };

  /**
   * Close modal
   */
  const closeModal = () => {
    setSelectedCall(null);
    setVideoUrl(null);
    setAiSummary([]);
    setAiTranscript(null);
    setCallNotes(null);
    setSnapshots([]);
    setShowVideoPlayer(false);
    setVideoCallDetails(null);
  };

  /**
   * Check subscription access (trial users can access)
   */
  if (!canAccess) {
    return (
      <SubscriptionRequiredScreen
        feature="Call History"
        reason={reason}
        onUpgrade={() => navigate('/profile?tab=billing')}
      />
    );
  }

  /**
   * Render loading state
   */
  if (loading && calls.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-[calc(100vh-8rem)] space-y-4">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <p className="text-muted-foreground">Loading call history...</p>
      </div>
    );
  }

  /**
   * Render error state
   */
  if (error && calls.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-[calc(100vh-8rem)] space-y-4">
        <p className="text-destructive">{error}</p>
        <Button onClick={() => refresh()} variant="outline">
          <RefreshCw className="h-4 w-4 mr-2" />
          Retry
        </Button>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-4 md:p-6 max-w-6xl">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold mb-2">Call History</h1>
        <p className="text-muted-foreground">View and manage your call recordings</p>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as 'all' | 'recording-request' | 'incoming-request')} className="mb-4">
        <TabsList className="grid w-full max-w-xl grid-cols-3">
          <TabsTrigger value="all">All Calls</TabsTrigger>
          <TabsTrigger value="recording-request">Request Recording</TabsTrigger>
          <TabsTrigger value="incoming-request">Incoming Requests</TabsTrigger>
        </TabsList>
      </Tabs>

      {/* Incoming Requests Tab */}
      {activeTab === 'incoming-request' ? (
        loadingIncoming ? (
          <div className="flex justify-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : incomingRequests.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 space-y-2">
            <p className="text-lg text-muted-foreground">No incoming requests yet</p>
            <p className="text-sm text-muted-foreground">
              Share your public intake link to receive customer submissions
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {incomingRequests.map((req: IncomingRequest) => {
              const customerName = `${req.first_name} ${req.last_name || ''}`.trim();
              const isVideo = req.path_type === 'video';
              return (
                <Card key={req.id} className="hover:shadow-md transition-shadow">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1 space-y-2">
                        <h3 className="font-semibold text-base">{customerName}</h3>
                        <div className="flex items-center gap-3 text-sm text-muted-foreground">
                          <span>{req.phone_number}</span>
                          {req.project && <span>· {req.project}</span>}
                        </div>
                        <p className="text-xs text-muted-foreground">
                          {formatDateTime(req.created_at)}
                        </p>
                      </div>
                      <div className="flex flex-col items-end gap-2">
                        <Badge className={isVideo
                          ? 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300'
                          : 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                        }>
                          {isVideo ? (
                            <><Video className="h-3 w-3 mr-1" /> Video</>
                          ) : (
                            <><Calendar className="h-3 w-3 mr-1" /> Scheduled</>
                          )}
                        </Badge>
                        {req.scheduled_time && (
                          <span className="text-xs text-muted-foreground">
                            {formatDateTime(req.scheduled_time)}
                          </span>
                        )}
                        {isVideo && req.room_id && req.status === 'completed' && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() =>
                              openRecordingByRoomId(req.room_id!, {
                                customerName,
                                phone: req.phone_number,
                                project: req.project,
                              })
                            }
                          >
                            View Video
                          </Button>
                        )}
                        {!isVideo && req.room_id && req.status === 'completed' && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => navigate(`/join/${req.room_id}?host=1`)}
                          >
                            Join Call
                          </Button>
                        )}
                        <Badge variant="outline" className="text-xs capitalize">
                          {req.status.replace('_', ' ')}
                        </Badge>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )
      ) : (
      <>

      {/* Search and Sort Bar */}
      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        {/* Search */}
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search calls..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
          />
        </div>

        {/* Filter by User */}
        {userRole === 'admin' && (
          <Select
            value={filterByUser || 'all'}
            onValueChange={(value) => setFilterByUser(value === 'all' ? undefined : value)}
            disabled={loadingTeam}
          >
            <SelectTrigger className="w-full sm:w-[200px]">
              <SelectValue placeholder="All Calls" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Calls</SelectItem>
              {teamMembers.map((member) => (
                <SelectItem key={member.id} value={member.id}>
                  {member.name} ({member.role})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}

        {/* Sort Button */}
        <Button
          variant="outline"
          onClick={toggleSort}
          className={sortOrder ? 'bg-primary/10' : ''}
        >
          <ArrowUpDown className="h-4 w-4 mr-2" />
          {sortOrder === 'asc' ? 'Oldest First' : sortOrder === 'desc' ? 'Newest First' : 'Sort'}
        </Button>

        {/* Refresh Button */}
        <Button
          variant="outline"
          onClick={() => refresh()}
          disabled={refreshing}
        >
          <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
        </Button>
      </div>

      {/* Calls List */}
      {tabFilteredCalls.length === 0 ? (
        <div className="flex flex-col items-center justify-center h-64 space-y-2">
          <p className="text-lg text-muted-foreground">
            {searchQuery
              ? 'No calls found'
              : activeTab === 'recording-request'
                ? 'No recording requests yet'
                : 'No calls yet'}
          </p>
          <p className="text-sm text-muted-foreground">
            {searchQuery
              ? 'Try adjusting your search'
              : activeTab === 'recording-request'
                ? 'Recording requests will appear here when you send them'
                : 'Your call history will appear here'}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {tabFilteredCalls.map((call) => {
            const badge = getCallTypeBadge(call.type);

            return (
              <Card
                key={call.id}
                className="hover:shadow-md transition-shadow cursor-pointer"
                onClick={() => handleCallClick(call)}
              >
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 space-y-2">
                      {/* Customer Name */}
                      <h3 className="font-semibold text-base">
                        {call.customer_name || 'Unknown Customer'}
                      </h3>

                      {/* Phone and Email */}
                      <div className="flex items-center gap-3 text-sm text-muted-foreground">
                        {(call.phone_number || call.customer_phone) && (
                          <span>
                            {call.phone_number || call.customer_phone}
                          </span>
                        )}
                        {(call.email || call.customer_email) && (
                          <span className="truncate">
                            | {call.email || call.customer_email}
                          </span>
                        )}
                      </div>

                      {/* Date/Time */}
                      <div className="flex items-center gap-3 text-sm text-muted-foreground">
                        <span>
                          {formatDateTime(call.start_time || call.created_at)}
                        </span>
                      </div>
                    </div>

                    {/* Call Type Badge */}
                    <div className="ml-4">
                      <Badge className={badge.color}>{badge.label}</Badge>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Load More Button */}
      {hasMore && !searchQuery && (
        <div className="flex justify-center mt-6">
          <Button onClick={loadMore} variant="outline">
            Load More
          </Button>
        </div>
      )}

      </>
      )}

      {/* Call Details Modal - Redesigned to match SessionSummaryPage */}
      <Dialog open={!!selectedCall} onOpenChange={(open) => !open && closeModal()}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle>Call Details</DialogTitle>
          </DialogHeader>

          {loadingCallData ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="h-10 w-10 animate-spin text-primary" />
            </div>
          ) : selectedCall ? (
            <div className="space-y-4 pb-4">
                {/* Colored Metric Cards (matching SessionSummaryPage) */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {/* Customer Card */}
                  <div className="p-4 bg-blue-50 rounded-lg">
                    <p className="text-sm text-blue-600 font-medium mb-1">Customer</p>
                    <p className="text-lg font-semibold text-gray-900">
                      {selectedCall.customer_name || 'Unknown'}
                    </p>
                    <p className="text-sm text-gray-600 mt-1">
                      {selectedCall.phone_number || selectedCall.customer_phone || 'No phone'}
                    </p>
                  </div>

                  {/* Duration Card */}
                  <div className="p-4 bg-green-50 rounded-lg">
                    <p className="text-sm text-green-600 font-medium mb-1">Duration</p>
                    <p className="text-lg font-semibold text-gray-900">
                      {formatDuration(getCallDuration(selectedCall))}
                    </p>
                    <p className="text-sm text-gray-600 mt-1">
                      {formatDateTime(selectedCall.start_time || selectedCall.created_at)}
                    </p>
                  </div>

                  {/* Recording Card */}
                  <div className="p-4 bg-purple-50 rounded-lg">
                    <p className="text-sm text-purple-600 font-medium mb-1">Recording</p>
                    <p className="text-lg font-semibold text-gray-900">
                      {selectedCall.has_recording || allRecordings.length > 0
                        ? 'Available'
                        : selectedCall.recording_status === 'recording'
                          ? 'Processing'
                          : 'Not saved'}
                    </p>
                    {!selectedCall.has_recording && allRecordings.length === 0 && (
                      <p className="text-sm text-gray-500 mt-1">
                        {selectedCall.recording_status === 'recording'
                          ? 'Recording is being uploaded...'
                          : 'No recording available'}
                      </p>
                    )}
                  </div>
                </div>

                {/* Join Now Button - Show for active/ongoing calls within 5 minutes (NOT for recording-request) */}
                {(() => {
                  // ✅ NEW: Don't show "Join Call Now" for recording-request calls
                  if (selectedCall.call_type === 'recording-request') {
                    return (
                      <div className="p-4 bg-orange-50 border border-orange-200 rounded-lg">
                        <p className="text-sm font-medium text-orange-900">Recording Request</p>
                        <p className="text-sm text-orange-700 mt-1">
                          This was a recording request. The video will be available once the customer uploads it.
                        </p>
                      </div>
                    );
                  }

                  // ✅ Check if call has ended
                  if ((selectedCall as any).ended_at) return null;

                  // Check if call is still joinable (no duration or ongoing, and within 5 mins of start time)
                  const isOngoing = !selectedCall.duration_seconds || selectedCall.status === 'ongoing';
                  if (!isOngoing || !selectedCall.room_id) return null;

                  // Check if within 5 minutes of start time
                  const startTime = new Date(selectedCall.start_time || selectedCall.created_at);
                  const now = new Date();
                  const minutesSinceStart = Math.floor((now.getTime() - startTime.getTime()) / 60000);

                  if (minutesSinceStart > 5) return null;

                  return (
                    <div>
                      <Button
                        variant="default"
                        onClick={() => {
                          navigate(`/join/${selectedCall.room_id}?host=1`);
                          setSelectedCall(null);
                        }}
                        className="w-full bg-green-600 hover:bg-green-700"
                      >
                        <svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                        </svg>
                        Join Call Now
                      </Button>
                    </div>
                  );
                })()}

                {/* View Recording Button(s) */}
                {(selectedCall.has_recording || allRecordings.length > 0) && (
                  <div className="space-y-2">
                    <Label className="text-base mb-2 block">
                      {allRecordings.length > 1 ? `Recordings (${allRecordings.length})` : 'Recording'}
                    </Label>
                    {allRecordings.length > 1 ? (
                      // Multiple recordings - show list
                      <div className="space-y-2">
                        {allRecordings.map((recording, index) => (
                          <Button
                            key={recording.id}
                            variant="outline"
                            onClick={() => {
                              console.log('[CallsPage] Playing recording:', recording);
                              console.log('[CallsPage] Recording URL:', recording.url);
                              if (!recording.url) {
                                toast({
                                  title: "Error",
                                  description: "Recording URL not available",
                                  variant: "destructive",
                                });
                                return;
                              }
                              setVideoUrl(recording.url);
                              setVideoCallDetails(selectedCall);
                              setSelectedCall(null);
                              setTimeout(() => {
                                setShowVideoPlayer(true);
                              }, 100);
                            }}
                            className="w-full justify-start"
                            disabled={!recording.url}
                          >
                            <svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            Recording {index + 1}
                            {recording.duration_seconds && (
                              <span className="ml-auto text-xs text-gray-500">
                                {Math.floor(recording.duration_seconds / 60)}:{String(recording.duration_seconds % 60).padStart(2, '0')}
                              </span>
                            )}
                          </Button>
                        ))}
                      </div>
                    ) : (
                      // Single recording - show single button
                      <Button
                        variant="default"
                        onClick={() => {
                          setVideoCallDetails(selectedCall);
                          setSelectedCall(null);
                          setTimeout(() => {
                            setShowVideoPlayer(true);
                          }, 100);
                        }}
                        className="w-full bg-purple-600 hover:bg-purple-700"
                      >
                        <svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        View Recording
                      </Button>
                    )}
                  </div>
                )}

                {/* View Customer Profile Button */}
                <div>
                  <Button
                    variant="default"
                    onClick={() => handleViewCustomer(selectedCall)}
                    className="w-full"
                  >
                    <UserIcon className="h-4 w-4 mr-2" />
                    View Customer Profile
                  </Button>
                </div>

                {/* Call Notes - Editable with auto-save */}
                <div>
                  <Label htmlFor="call-notes" className="text-base mb-2 block">Call Notes (Optional)</Label>
                  <Textarea
                    id="call-notes"
                    value={callNotes || ""}
                    onChange={(e) => setCallNotes(e.target.value)}
                    placeholder="Add any notes about this call..."
                    rows={3}
                    className="mb-2"
                  />
                  {callNotes && callNotes.trim().length > 0 && (
                    <div className="flex items-center gap-1.5 text-sm text-green-600 mt-2">
                      <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></div>
                      <span className="font-medium">Auto-saved</span>
                    </div>
                  )}
                </div>

                {/* AI Summary Section (matching SessionSummaryPage) */}
                {aiSummary.length > 0 && (
                  <div>
                    <Label className="text-base mb-3 block flex items-center gap-2">
                      <Sparkles className="h-5 w-5 text-purple-600" />
                      AI Summary
                    </Label>
                    <div className="space-y-2">
                      {aiSummary.map((point, index) => (
                        <div key={index} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                          <div className="w-6 h-6 bg-blue-500 text-white rounded-full flex items-center justify-center flex-shrink-0 text-sm font-medium">
                            {index + 1}
                          </div>
                          <p className="text-sm text-gray-700 leading-relaxed">{point}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Transcript */}
                {aiTranscript && (
                  <Dialog>
                    <DialogTrigger asChild>
                      <Button variant="outline" className="w-full">View Full Transcript</Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-4xl max-h-[80vh]">
                      <DialogHeader>
                        <DialogTitle>Call Transcript</DialogTitle>
                        <DialogDescription>Complete conversation transcript</DialogDescription>
                      </DialogHeader>
                      <ScrollArea className="h-[60vh] pr-4">
                        <div className="whitespace-pre-wrap text-sm leading-relaxed">
                          {aiTranscript}
                        </div>
                      </ScrollArea>
                    </DialogContent>
                  </Dialog>
                )}

                {/* Snapshots Grid (matching SessionSummaryPage) */}
                {snapshots.length > 0 && (
                  <div>
                    <Label className="text-base mb-3 block">Call Snapshots ({snapshots.length})</Label>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      {snapshots.map((s) => (
                        <button
                          key={s.name}
                          onClick={() => setSelectedImage(s)}
                          className="rounded-lg border-2 border-gray-200 overflow-hidden bg-white hover:border-blue-400 transition-colors group cursor-pointer"
                        >
                          <img
                            src={s.url}
                            alt={s.name}
                            className="w-full h-32 object-cover"
                            loading="lazy"
                          />
                          <div className="p-2 bg-gray-50 group-hover:bg-blue-50 transition-colors">
                            <span className="text-xs text-gray-600 group-hover:text-blue-600">
                              View
                            </span>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
          ) : null}
        </DialogContent>
      </Dialog>

      {/* Video Player Modal */}
      {videoUrl && videoCallDetails && (
        <VideoPlayerModal
          open={showVideoPlayer}
          onOpenChange={setShowVideoPlayer}
          videoUrl={videoUrl}
          callDetails={{
            customerName: videoCallDetails.customer_name,
            customerEmail: videoCallDetails.customer_email || videoCallDetails.email,
            customerPhone: videoCallDetails.customer_phone || videoCallDetails.phone_number,
            startTime: videoCallDetails.start_time,
            duration: videoCallDetails.duration_seconds || videoCallDetails.duration,
            type: videoCallDetails.type,
            notes: videoCallDetails.notes || undefined,
          }}
        />
      )}


      {/* Image Modal */}
      {selectedImage && (
        <ImageModal
          open={!!selectedImage}
          onOpenChange={(open) => !open && setSelectedImage(null)}
          imageUrl={selectedImage.url}
          imageName={selectedImage.name}
        />
      )}
    </div>
  );
}
